/*!
// <copyright file="OfficeInteraction.js" company="Microsoft">
//     Copyright (c) Microsoft Corporation.  All rights reserved.
// </copyright>
*/

import { util, appletMessaging } from "@ax/office-shared-lib";
import { telemetry } from "@ax/office-cdn-app-telemetry";
import { appletInteraction } from "./AppletInteraction";
import { resources } from "@ax/office-cdn-app-resources";
import { appState } from "./AppState";

export namespace officeInteraction {

    /** Maps an event type to an array of IDs representing the applets subscribed to that event. */
    export let documentEventSubscriptionMap: util.PropertyBag<string[]>;

    /** Maps a binding ID to another map that maps an event type to an array of IDs representing the applets subscribed to that event on the binding. */
    export let bindingEventSubscriptionMap: util.PropertyBag<util.PropertyBag<string[]>>;

    /* jshint -W069 */
    /** Represents the type of methods that can be executed by the Office interaction code. */
    export enum MethodType {

        /** Represents document event subscription methods. */
        DocumentEventSubscription = "DocumentEventSubscription",

        /** Represents binding event subscription methods. */
        BindingEventSubscription = "BindingEventSubscription",

        /** Represents Office action methods. */
        OfficeAction = "OfficeAction",

        /** Represents document methods. */
        DocumentMethod = "DocumentMethod",

        /** Represents bindings methods. */
        BindingsMethod = "BindingsMethod",

        /** Represents binding methods. */
        BindingMethod = "BindingMethod"
    }
    /* jshint +W069 */

    interface DocumentEvent {
        // tslint:disable-next-line:no-reserved-keywords
        type: Office.EventType;
    }

    /** The base document selection event. */
    export let _documentSelectionChangedEvent: DocumentEvent;

    interface BindingEvent {
        // tslint:disable-next-line:no-reserved-keywords
        type: Office.EventType;
        binding: Office.Binding;
    }

    interface BindingSelectionChangedEvent extends BindingEvent {
        startColumn: number;
        columnCount: number;
        startRow: number;
        rowCount: number;
    }

    /** The base binding selection event. */
    export let _bindingSelectionChangedEvent: BindingSelectionChangedEvent;

    /** Initializes the data structures and message handling necessary for interacting with applets. */
    export function initialize(): void {

        // Initialize empty maps
        officeInteraction.documentEventSubscriptionMap = {};
        officeInteraction.bindingEventSubscriptionMap = {};

        officeInteraction._documentSelectionChangedEvent = {
            type: Office.EventType.DocumentSelectionChanged
        };

        officeInteraction._bindingSelectionChangedEvent = {
            type: Office.EventType.BindingSelectionChanged,
            binding: {
                id: ""
            } as Office.Binding,
            columnCount: 0,
            startColumn: 0,
            rowCount: 0,
            startRow: 0
        };

        // Add listeners for applet messages
        appletInteraction.registerAppletRequestHandler(appletMessaging.MessageTypes.DocumentEventSubscription, officeInteraction.handleDocumentEventSubscriptionRequest);
        appletInteraction.registerAppletRequestHandler(appletMessaging.MessageTypes.BindingEventSubscription, officeInteraction.handleBindingEventSubscriptionRequest);
        appletInteraction.registerAppletRequestHandler(appletMessaging.MessageTypes.OfficeAction, officeInteraction.handleOfficeActionRequest);
        appletInteraction.registerAppletRequestHandler(appletMessaging.MessageTypes.DocumentMethod, officeInteraction.handleDocumentMethodRequest);
        appletInteraction.registerAppletRequestHandler(appletMessaging.MessageTypes.BindingsMethod, officeInteraction.handleBindingsMethodRequest);
        appletInteraction.registerAppletRequestHandler(appletMessaging.MessageTypes.BindingMethod, officeInteraction.handleBindingMethodRequest);
    }

    /**
     * Called when any subscribed document event fires and forwards the event details to the subscribed applets.
     * @param eventArgs Information about the document that raised the event.
     */
    export function handleDocumentEvent(eventArgs: DocumentEvent): void {

        // If the event was a document selection changed event, schedule it appropriately; otherwise send the event immediately.
        if (eventArgs.type === Office.EventType.DocumentSelectionChanged) {
            officeInteraction.sendDocumentEventToApplet(officeInteraction._documentSelectionChangedEvent, false);
        } else {
            officeInteraction.sendDocumentEventToApplet(eventArgs);
        }
    }

    /**
     * Sends the provided document event arguments to subscribed applets.
     * @param eventArgs The document event arguments to send.
     */
    export function sendDocumentEventToApplet(eventArgs: DocumentEvent, cloneData?: boolean): void {

        const subscribedApplets: string[] = officeInteraction.documentEventSubscriptionMap[eventArgs.type];

        if (subscribedApplets !== undefined) {
            for (const appletID of subscribedApplets) {
                const request = new appletMessaging.Requests.OfficeEventRequest(appletID, appletMessaging.OfficeEventSource.Document, eventArgs);
                appletInteraction.sendMessageToApplet(appletID, request, cloneData);
            }
        }

        telemetry.events.documentEvent.log(eventArgs.type as any);
    }

    /**
     * Called when any subscribed binding event fires and forwards the event details to the subscribed applets.
     * @param eventArgs Information about the binding that raised the event.
     */
    export function handleBindingEvent(eventArgs: BindingEvent): void {

        // If the event was a binding selection changed event, schedule it appropriately; otherwise send the event immediately.
        if (eventArgs.type === Office.EventType.BindingSelectionChanged) {
            const selectionChangedEventArgs = eventArgs as BindingSelectionChangedEvent;
            officeInteraction._bindingSelectionChangedEvent.binding.id = eventArgs.binding.id;
            if ("columnCount" in eventArgs) {
                officeInteraction._bindingSelectionChangedEvent.columnCount = selectionChangedEventArgs.columnCount;
                officeInteraction._bindingSelectionChangedEvent.startColumn = selectionChangedEventArgs.startColumn;
                officeInteraction._bindingSelectionChangedEvent.rowCount = selectionChangedEventArgs.rowCount;
                officeInteraction._bindingSelectionChangedEvent.startRow = selectionChangedEventArgs.startRow;
            } else {
                delete officeInteraction._bindingSelectionChangedEvent.columnCount;
                delete officeInteraction._bindingSelectionChangedEvent.startColumn;
                delete officeInteraction._bindingSelectionChangedEvent.rowCount;
                delete officeInteraction._bindingSelectionChangedEvent.startRow;
            }
            officeInteraction.sendBindingEventToApplet(officeInteraction._bindingSelectionChangedEvent, false);
        } else {
            officeInteraction.sendBindingEventToApplet(eventArgs);
        }
    }

    /**
     * Sends the provided binding event arguments to subscribed applets.
     * @param eventArgs The binding event arguments to send.
     */
    export function sendBindingEventToApplet(eventArgs: BindingEvent, cloneData?: boolean): void {

        // bindingEventSubscriptionMap has two levels of mapping: first mapped by binding ID, then by event type
        const eventTypeMap = officeInteraction.bindingEventSubscriptionMap[eventArgs.binding.id];

        if (eventTypeMap !== undefined) {
            const subscribedApplets = eventTypeMap[eventArgs.type];
            if (subscribedApplets !== undefined) {
                for (const appletID of subscribedApplets) {
                    const request = new appletMessaging.Requests.OfficeEventRequest(appletID, appletMessaging.OfficeEventSource.Binding, eventArgs);
                    appletInteraction.sendMessageToApplet(appletID, request, cloneData);
                }
            }
        }

        telemetry.events.bindingEvent.log(eventArgs.type as any);
    }

    /**
     * Unsubscribes the provided applet from all document events.
     * @param appletID The ID of the applet of which to unsubscribe from all document events.
     */
    export function unsubscribeAllDocumentEvents(appletID: string): void {
        for (const eventType in officeInteraction.documentEventSubscriptionMap) {
            officeInteraction.unsubscribeDocumentEvent(appletID, eventType as any);
        }
    }

    /**
     * Unsubscribes the provided applet from the provided document event.
     * @param appletID The ID of the applet of which to unsubscribe from a document event.
     * @param eventType The type of document event from which to unsubscribe.
     */
    export function unsubscribeDocumentEvent(appletID: string, eventType: Office.EventType): void {
        const subscribedApplets = officeInteraction.documentEventSubscriptionMap[eventType];
        if (subscribedApplets !== undefined) {
            const appletIndex = subscribedApplets.indexOf(appletID);
            if (appletIndex !== -1) {
                subscribedApplets.splice(appletIndex, 1);
            }
        }
    }

    /**
     * Unsubscribes the provided applet from all binding events.
     * @param appletID The ID of the applet of which to unsubscribe from all binding events.
     */
    export function unsubscribeAllBindingEvents(appletID: string): void {
        for (const bindingID in officeInteraction.bindingEventSubscriptionMap) {
            const eventTypeMap = officeInteraction.bindingEventSubscriptionMap[bindingID];
            if (eventTypeMap !== undefined) {
                for (const eventType in eventTypeMap) {
                    officeInteraction.unsubscribeBindingEvent(appletID, bindingID, eventType as any);
                }
            }
        }
    }

    /**
     * Unsubscribes the provided applet from the provided binding's provided event.
     * @param appletID The ID of the applet of which to unsubscribe from a binding event.
     * @param bindingID The ID of the binding of which to unsubscribe an event.
     * @param eventType The type of binding event from which to unsubscribe.
     */
    export function unsubscribeBindingEvent(appletID: string, bindingID: string, eventType: Office.EventType): void {
        const eventTypeMap = officeInteraction.bindingEventSubscriptionMap[bindingID];
        if (eventTypeMap !== undefined) {
            const subscribedApplets = eventTypeMap[eventType];
            if (subscribedApplets !== undefined) {
                const appletIndex = subscribedApplets.indexOf(appletID);
                if (appletIndex !== -1) {
                    subscribedApplets.splice(appletIndex, 1);
                }
            }
        }
    }

    /**
     * Handles the appletMessaging.Requests.DocumentEventSubscriptionRequest event and subscribes the applet to the specified document events.
     * @param event The request event.
     */
    export function handleDocumentEventSubscriptionRequest(event: util.AddinMessageEvent<appletMessaging.Requests.DocumentEventSubscriptionRequest>): void {

        const callbackHandler = new officeInteraction.HandleDocumentEventSubscriptionRequestCallbackHandler(event);

        const documentEventSubscriptions = event.data.Data.DocumentEventSubscriptions;
        for (let index = 0; index < documentEventSubscriptions.length; index++) {
            const eventType = documentEventSubscriptions[index];
            if (eventType in officeInteraction.documentEventSubscriptionMap) {
                // If a subscription has already been made for the event type, call _addHandlerCallback with a mock successful result
                callbackHandler._addHandlerCallback({
                    status: Office.AsyncResultStatus.Succeeded,
                    asyncContext: { Index: index, EventType: eventType }
                } as Office.AsyncResult<any>);
            } else {
                Office.context.document.addHandlerAsync(
                    eventType as any,
                    officeInteraction.handleDocumentEvent,
                    { asyncContext: { Index: index, EventType: eventType } },
                    callbackHandler._addHandlerCallback);
            }
        }
    }

    /** Handles callbacks for the officeInteraction.handleDocumentEventSubscriptionRequest method. */
    export class HandleDocumentEventSubscriptionRequestCallbackHandler {

        /** The request event. */
        private event: util.AddinMessageEvent<appletMessaging.Requests.DocumentEventSubscriptionRequest>;

        /** The document events to subscribe to. */
        private documentEventSubscriptions: string[];

        private results: Office.AsyncResult<any>[];
        private expectedResultCount: number;
        private receivedResultCount: number;
        private failedResponseReceived: boolean;

        /**
         * @param event The request event.
         */
        constructor(event: util.AddinMessageEvent<appletMessaging.Requests.DocumentEventSubscriptionRequest>) {
            this.event = event;
            this.documentEventSubscriptions = event.data.Data.DocumentEventSubscriptions;
            this.results = [];
            this.expectedResultCount = this.documentEventSubscriptions.length;
            this.receivedResultCount = 0;
            this.failedResponseReceived = false;

            // Initialize the Results array so that the received AsyncResults can be set at the index matching the requests'
            while (this.results.length !== this.expectedResultCount) {
                this.results.push(undefined);
            }

            this._addHandlerCallback = this._addHandlerCallback.bind(this);
        }

        /**
         * Sets the result of the addHandlerAsync call in the response's array of results and sends a response message to the
         * applet if all expected results have been received.
         * @param asyncResult The result of the addHandlerAsync call.
         */
        public _addHandlerCallback(asyncResult: Office.AsyncResult<any>): void {

            // asyncContext.Index is the index at which the result should be set in the Results array
            const index = asyncResult.asyncContext.Index;
            this.results[index] = asyncResult;
            this.receivedResultCount++;

            if (asyncResult.status === Office.AsyncResultStatus.Failed) {
                this.failedResponseReceived = true;
                telemetry.events.officeApiFailure.log(
                    MethodType.DocumentEventSubscription,
                    (asyncResult.asyncContext || {} as any).EventType,
                    (asyncResult.error || {} as any).code || -1);
            } else {
                // Add the applet to the list of subscribed applets if the subscription was a success
                let subscribedApplets = officeInteraction.documentEventSubscriptionMap[asyncResult.asyncContext.EventType];

                if (subscribedApplets === undefined) {
                    subscribedApplets = [];
                    officeInteraction.documentEventSubscriptionMap[asyncResult.asyncContext.EventType] = subscribedApplets;
                }

                subscribedApplets.push(this.event.data.AppletID);
            }

            // Send the response if every result has been received
            if (this.receivedResultCount === this.expectedResultCount) {
                const requestData = this.event.data.Data;
                const response = new appletMessaging.Responses.DocumentEventSubscriptionResponse(this.event.data);
                response.Data.Results = this.results;

                if (this.failedResponseReceived) {
                    response.Status = appletMessaging.ResponseStatus.failure;
                    response.FailureMessage = resources.officeInteraction.appletDocumentSubscriptionErrorFormat(this.documentEventSubscriptions[index]);
                } else {
                    response.Status = appletMessaging.ResponseStatus.success;
                }

                appletInteraction.sendMessageToApplet(this.event.data.AppletID, response);
            }
        }
    }

    /**
     * Handles the appletMessaging.Requests.BindingEventSubscriptionRequest event and subscribes the applet to the specified binding events.
     * @param event The request event.
     */
    export function handleBindingEventSubscriptionRequest(event: util.AddinMessageEvent<appletMessaging.Requests.BindingEventSubscriptionRequest>): void {
        const callbackHandler = new officeInteraction.HandleBindingEventSubscriptionRequestCallbackHandler(event);
        Office.context.document.bindings.getByIdAsync(event.data.Data.BindingID, {}, callbackHandler._getByIdCallback);
    }

    /** Handles callbacks for the officeInteraction.handleBindingEventSubscriptionRequest method. */
    export class HandleBindingEventSubscriptionRequestCallbackHandler {

        /** The request object. */
        private event: util.AddinMessageEvent<appletMessaging.Requests.BindingEventSubscriptionRequest>;

        private eventTypes: Office.EventType[];
        private results: Office.AsyncResult<any>[];
        private receivedResultCount: number;
        private failedResponseReceived: boolean;

        /**
         * @param event The request object.
         */
        constructor(event: util.AddinMessageEvent<appletMessaging.Requests.BindingEventSubscriptionRequest>) {
            this.event = event;
            this.eventTypes = event.data.Data.BindingEventSubscriptions as any;
            this.results = [];
            this.receivedResultCount = 0;
            this.failedResponseReceived = false;

            this._getByIdCallback = this._getByIdCallback.bind(this);
            this._addHandlerCallback = this._addHandlerCallback.bind(this);
        }

        /**
         * Upon successful retrieval of the binding, attempts to add an event handler for every requested event. If binding retrieval
         * failed, the failed, an empty set of results is sent to the applet.
         * @param asyncResult The result of the getById call.
         */
        public _getByIdCallback(asyncResult: Office.AsyncResult<any>): void {

            if (asyncResult.status === Office.AsyncResultStatus.Failed) {
                const response = new appletMessaging.Responses.BindingEventSubscriptionResponse(this.event.data);
                response.Status = appletMessaging.ResponseStatus.failure;
                response.FailureMessage = resources.officeInteraction.appletTableBindingRetrievalError();
                response.Data.Results = this.results;
                telemetry.events.officeApiFailure.log(
                    MethodType.BindingEventSubscription,
                    "getByIdAsync",
                    (asyncResult.error || {} as any).code || -1);
                appletInteraction.sendMessageToApplet(this.event.data.AppletID, response);
            } else {
                const binding = asyncResult.value;

                // Initialize the Results array so that the received AsyncResults can be set at the index matching the requests'
                while (this.results.length !== this.eventTypes.length) {
                    this.results.push(undefined);
                }

                // bindingEventSubscriptionMap has two levels of mapping: first mapped by binding ID, then by event type
                let eventTypeMap = officeInteraction.bindingEventSubscriptionMap[binding.id];

                if (eventTypeMap === undefined) {
                    eventTypeMap = {};
                    officeInteraction.bindingEventSubscriptionMap[binding.id] = eventTypeMap;
                }

                for (let index = 0; index < this.eventTypes.length; index++) {
                    const eventType = this.eventTypes[index];
                    if (eventType in eventTypeMap) {
                        // If a subscription has already been made for the event type, call _addHandlerCallback with a mock successful result
                        this._addHandlerCallback({
                            status: Office.AsyncResultStatus.Succeeded,
                            asyncContext: { Index: index, EventType: eventType }
                        } as Office.AsyncResult<any>);
                    } else {
                        binding.addHandlerAsync(
                            eventType,
                            officeInteraction.handleBindingEvent,
                            { asyncContext: { Index: index, EventType: eventType } },
                            this._addHandlerCallback);
                    }

                }
            }
        }

        private _addHandlerCallback(asyncResult: Office.AsyncResult<any>): void {
            /// <summary>
            /// Sets the result of the addHandlerAsync call in the response's array of results and sends a response message to the
            /// applet if all expected results have been received.
            /// </summary>
            /// <param name="asyncResult" type="AsyncResult">The result of the addHandlerAsync call.</param>

            // asyncContext.Index is the index at which the result should be set in the Results array
            const index = asyncResult.asyncContext.Index;
            this.results[index] = asyncResult;
            this.receivedResultCount++;

            if (asyncResult.status === Office.AsyncResultStatus.Failed) {
                this.failedResponseReceived = true;
                telemetry.events.officeApiFailure.log(
                    MethodType.BindingEventSubscription,
                    (asyncResult.asyncContext || {} as any).EventType,
                    (asyncResult.error || {} as any).code || -1);
            } else {
                // Add the applet to the list of subscribed applets if the subscription was a success
                let eventTypeMap = officeInteraction.bindingEventSubscriptionMap[this.event.data.Data.BindingID];

                if (eventTypeMap === undefined) {
                    eventTypeMap = {};
                    officeInteraction.bindingEventSubscriptionMap[this.event.data.Data.BindingID] = eventTypeMap;
                }

                let subscribedApplets = eventTypeMap[asyncResult.asyncContext.EventType];

                if (subscribedApplets === undefined) {
                    subscribedApplets = [];
                    eventTypeMap[asyncResult.asyncContext.EventType] = subscribedApplets;
                }

                subscribedApplets.push(this.event.data.AppletID);
            }

            // Send the response if every result has been received
            if (this.receivedResultCount === this.eventTypes.length) {
                const requestData = this.event.data.Data;
                const response = new appletMessaging.Responses.BindingEventSubscriptionResponse(this.event.data);
                response.Data.Results = this.results;

                if (this.failedResponseReceived) {
                    response.Status = appletMessaging.ResponseStatus.failure;
                    response.FailureMessage = resources.officeInteraction.appletTableBindingSubscriptionErrorFormat(requestData.BindingEventSubscriptions[index]);
                } else {
                    response.Status = appletMessaging.ResponseStatus.success;
                }

                appletInteraction.sendMessageToApplet(this.event.data.AppletID, response);
            }
        }
    }

    /**
     * Handles the appletMessaging.Requests.OfficeActionRequest event and executes the specified actions. These actions require Office 2016.
     * @param event The request event.
     */
    export function handleOfficeActionRequest(event: util.AddinMessageEvent<appletMessaging.Requests.OfficeActionRequest>): void {

        // If the version of Office is prior to 2016 then display an error, unload the source applet, and return early
        if (appState.State.OfficeVersion < util.OfficeVersion.Release) {
            telemetry.errors.officeVersionRequirementsNotMet.show(resources.officeInteraction.office2016RequiredError(), util.OfficeVersion.Release, appState.State.OfficeVersion);
            appletInteraction.unloadApplet(event.data.AppletID);
            return;
        }

        const actionExecutor = new officeInteraction.HandleOfficeActionRequestOfficeActionExecutor(event.data);
        actionExecutor.continueOfficeActionExecution();
    }

    interface ResolveOfficeActionPathOptions {

        /** The value to be assigned to the final resolved path; used if present in the options, even if undefined. */
        AssignmentValue?: any
    }

    /** Executes appletMessaging.OfficeAction instances asynchronously. */
    export class HandleOfficeActionRequestOfficeActionExecutor {

        /** The request containing the actions to execute. */
        private request: appletMessaging.Requests.OfficeActionRequest;

        /** The index of the next action to execute in the request.Actions array. */
        private actionIndex: number;

        /** The request context being used to execute non-legacy actions. */
        private context: Excel.RequestContext | Word.RequestContext;

        /** The promise returned from the last context sync operation. */
        private contextPromise: OfficeExtension.IPromise<any>;

        /** The current AsyncResult from the last legacy call. */
        private currentAsyncResult: Office.AsyncResult<any>;

        /** A property bag used as the local scope accessible to the actions. */
        private localScope: util.PropertyBag<any>;

        /** A property bag containing the request return values of the actions. */
        private returnValues: util.PropertyBag<any>;

        /** The AsyncResults from all completed legacy calls. */
        private asyncResults: Office.AsyncResult<any>[];

        /** The error message encountered during action execution. */
        private errorMessage: string;

        /**
         * @param request The request containing the actions to execute.
         */
        constructor(request: appletMessaging.Requests.OfficeActionRequest) {
            this.request = request;
            this.actionIndex = 0;
            this.localScope = {};
            this.returnValues = {};
            this.asyncResults = [];

            this.continueOfficeActionExecution = this.continueOfficeActionExecution.bind(this);
            this._sendResponse = this._sendResponse.bind(this);
        }

        /**
         * Executes the next action queued in the request.
         * This code can be hard to understand with all of the helper functions, so here is a rundown of how it works. This class
         * executes OfficeActions targeting both the legacy API (under the Office namespace using AsyncResults) and the 2016 API
         * (under the Excel and Word namespaces using promises). The flow is structured around the recursive continueOfficeActionExecution
         * method which continually calls _executeOfficeAction and syncs the request context. Legacy calls are executed like so:
         *
         * 1. continueOfficeActionExecution starts execution or processes the previous AsyncResult, then calls _executeOfficeAction
         * 2. _executeOfficeAction calls the referenced legacy method and sends continueOfficeActionExecution as its callback
         * 3. Repeat after continueOfficeActionExecution is called as the callback
         *
         * 2016 API calls are executed like so:
         *
         * 1. continueOfficeActionExecution makes sure the context exists and calls _executeOfficeAction
         * 2. _executeOfficeAction calls the referenced method, gets the referenced property value, or sets the referenced property value
         * 3. continueOfficeActionExecution syncs the context if requested
         * 4. For every action after the first sync, _executeOfficeAction is queued on the promise as a then() clause instead of called directly
         * 5. continueOfficeActionExecution calls itself recursively so the promise is queued up with a bunch of then() clauses and a final catch()
         * @param asyncResult The result from the previous legacy call if applicable.
         */
        public continueOfficeActionExecution(asyncResult?: Office.AsyncResult<any>): void {

            // If this method is being called as a legacy callback with an AsyncResult, either stop execution due to error or store
            // the result of the previous operation in the asyncResults array
            if (asyncResult) {
                if (asyncResult.status === Office.AsyncResultStatus.Failed) {
                    this.errorMessage = asyncResult.error.message;
                    this._sendResponse();
                    const lastAction = this.request.Data.Actions[this.actionIndex - 1];
                    let legacyFailedResultPath: string;
                    if (lastAction) {
                        legacyFailedResultPath = (lastAction.Path || []).map((reference, index, references) => {
                            return reference.Name;
                        }).join(".");
                    }
                    telemetry.events.officeApiFailure.log(
                        MethodType.OfficeAction,
                        legacyFailedResultPath || "UNKNOWN",
                        asyncResult.error.code || -1);
                    return;
                } else {
                    this.currentAsyncResult = asyncResult;
                    this.asyncResults.push(asyncResult);
                }
            }

            // If there are no more actions to execute, finalize the promise if it exists; if not, send the response immediately
            if (this.actionIndex === this.request.Data.Actions.length) {
                if (this.contextPromise) {
                    this.contextPromise = this.contextPromise.then(this._sendResponse);
                    this._addCatchToCurrentPromise();
                } else {
                    this._sendResponse();
                }

                return;
            }

            // Retrieve the action and validate that the context has been created if needed
            const action = this.request.Data.Actions[this.actionIndex];
            const isLegacy = this._isLegacy(action);
            this._verifyContext(action);

            if (this.contextPromise) {
                // If a promise already exists, add a then() to execute the office action
                this.contextPromise = this.contextPromise.then(() => {
                    this._executeOfficeAction(action);
                });

                // If the action is legacy but a promise exists, finalize it and reset the contextPromise so another one is
                // created inside the Office method callback if more non-Office actions need to be executed or synced
                if (isLegacy) {
                    this._addCatchToCurrentPromise(action);
                    this.contextPromise = undefined;
                }
            } else {
                // Since no promise currently exists, the action can be executed immediately
                try {
                    this._executeOfficeAction(action);
                } catch (error) {
                    this.errorMessage = error.message;
                    this._sendResponse();
                    const pathString = (action.Path || []).map((reference, index, references) => {
                        return reference.Name;
                    }).join(".");
                    telemetry.events.officeApiFailure.log(
                        MethodType.OfficeAction,
                        pathString || "UNKNOWN",
                        error.code || -1);
                    return;
                }
            }

            // Only recognize the Sync value if the scope of the action is not legacy
            if (action.Sync && !isLegacy) {
                if (this.contextPromise) {
                    this.contextPromise = this.contextPromise.then(this.context.sync);
                } else {
                    this.contextPromise = this.context.sync();
                }
            }

            // Increment actionIndex so the next call of this method executes or queues the next action
            this.actionIndex++;

            // If the action is not legacy, the next action can be executed or queued on the promise immediately
            if (!isLegacy) {
                this.continueOfficeActionExecution();
            }
        }

        /**
         * Determines whether the provided action is intended for use with the legacy Office API.
         * @param action The action to check.
         * @returns A value indicating whether the provided action is intended for use with the legacy Office API.
         */
        private _isLegacy(action: appletMessaging.OfficeAction): boolean {
            return action.Scope === appletMessaging.OfficeActionScope.Office || action.Scope === appletMessaging.OfficeActionScope.AsyncResult;
        }

        /**
         * Verifies that <see cref="context"/> exists if the provided action requires it.
         * @param action The action to use to verify the context.
         */
        private _verifyContext(action: appletMessaging.OfficeAction): void {
            const contextNeeded = action.Scope === appletMessaging.OfficeActionScope.Context || action.Sync;

            // No context should be created if the action is legacy, as legacy actions do not use promises
            if (this.context === undefined && contextNeeded && !this._isLegacy(action)) {
                switch (appState.State.CurrentApplication) {
                    case util.ApplicationType.Excel:
                        this.context = new Excel.RequestContext();
                        break;
                    case util.ApplicationType.Word:
                        this.context = new Word.RequestContext();
                        break;
                    default:
                        throw new Error(resources.dynamicsApp.unrecognizedApplicationError());
                }
            }
        }

        /** Adds a catch() block to the current context promise so that all context exceptions are handled. */
        private _addCatchToCurrentPromise(action?: appletMessaging.OfficeAction): void {
            this.contextPromise.catch((error) => {
                this.errorMessage = error.message;
                this._sendResponse();
                let pathString: string;
                if (action) {
                    pathString = (action.Path || []).map((reference, index, references) => {
                        return reference.Name;
                    }).join(".");
                }
                telemetry.events.officeApiFailure.log(
                    MethodType.OfficeAction,
                    pathString || "UNKNOWN",
                    error.code || -1);
            });
        }

        /** Sends a response to the requesting applet. */
        private _sendResponse(): void {
            const response = new appletMessaging.Responses.OfficeActionResponse(this.request);

            // If no error was encountered, send back a successful response; otherwise, send back a failure response
            if (this.errorMessage === undefined) {
                response.Status = appletMessaging.ResponseStatus.success;
                response.Data.Values = this.returnValues;
                response.Data.Results = this.asyncResults;
            } else {
                response.Status = appletMessaging.ResponseStatus.failure;
                response.FailureMessage = this.errorMessage;
            }
            appletInteraction.sendMessageToApplet(this.request.AppletID, response);
        }

        /**
         * Resolves the path of the provided action, returning the resulting property or function result or assigning a value.
         * @param action The action to resolve.
         * @param options Optional parameters to use when resolving the path.
         * @returns The final resolved object or undefined if a value was assigned.
         */
        private _resolveOfficeActionPath(action: appletMessaging.OfficeAction, options: ResolveOfficeActionPathOptions = {}): any {

            this._ensureDataCacheActionValidity(action); // TFS #3739814: remove this call once the Office bug preventing us from accessing tables in inactive worksheets is fixed
            let currentObject;

            // Use the action's scope to determine the starting object
            switch (action.Scope) {
                case appletMessaging.OfficeActionScope.Context:
                    currentObject = this.context;
                    break;
                case appletMessaging.OfficeActionScope.Local:
                    currentObject = this.localScope;
                    break;
                case appletMessaging.OfficeActionScope.Office:
                    currentObject = Office;
                    break;
                case appletMessaging.OfficeActionScope.AsyncResult:
                    currentObject = this.currentAsyncResult;
                    break;
            }

            for (let pathIndex = 0; pathIndex < action.Path.length; pathIndex++) {
                const reference = action.Path[pathIndex];

                if (pathIndex === action.Path.length - 1 && "AssignmentValue" in options) {
                    // If this is the last path segment and a value is supposed to be assigned, assign it now and reset currentObject
                    currentObject[reference.Name] = options.AssignmentValue;
                    currentObject = undefined;
                } else {
                    let newObject = currentObject[reference.Name];

                    if (reference.Type === appletMessaging.OfficeReferenceType.Function) {
                        const args = reference.Arguments;

                        // If this is the final function being called and the action is legacy, add the continue function as a
                        // parameter to be called as a callback
                        if (pathIndex === action.Path.length - 1 && this._isLegacy(action)) {
                            args.push(this.continueOfficeActionExecution);
                        }

                        newObject = newObject.apply(currentObject, args);
                    }

                    currentObject = newObject;
                }
            }

            return currentObject;
        }

        /**
         * Executes the provided action.
         * @param action The action to execute.
         */
        private _executeOfficeAction(action: appletMessaging.OfficeAction): void {

            const options: ResolveOfficeActionPathOptions = {};

            // Only assign a value if AssignmentValue exists but VariableName is undefined, and the action is not legacy
            if (!this._isLegacy(action) && "AssignmentValue" in action && action.VariableName === undefined) {
                options.AssignmentValue = action.AssignmentValue;
            }

            const resolvedObject = this._resolveOfficeActionPath(action, options);

            // Save the resolved value in the local scope and returnValues objects as required
            if (action.VariableName !== undefined) {
                this.localScope[action.VariableName] = resolvedObject;

                if (action.ReturnResult) {
                    this.returnValues[action.VariableName] = resolvedObject;
                }
            }
        }

        /**
         * Determines whether the provided action has been requested by the data cache operation; if so, the action
         * is transformed so it can be executed properly.
         * @param action The action to verify.
         */
        private _ensureDataCacheActionValidity(action: appletMessaging.OfficeAction): void {

            if (action.Scope !== appletMessaging.OfficeActionScope.Context) {
                return;
            }

            if (action.Path.length >= 4 &&
                action.Path[0].Name === "workbook" &&
                action.Path[1].Name === "bindings" &&
                action.Path[2].Name === "getItem" &&
                action.Path[3].Name === "getTable" &&
                action.Path[2].Arguments.length === 1 &&
                (action.Path[2].Arguments[0] === "DataCacheKey" || action.Path[2].Arguments[0] === "DataCacheData")) {
                action.Path[1].Name = "tables";
                action.Path[2].Arguments[0] = action.Path[2].Arguments[0] === "DataCacheKey" ? "DataCacheKeyTable" : "DataCacheDataTable";
                action.Path.splice(3, 1);
                telemetry.events.dataCacheTableGetWorkaround.log();
            } else if (action.Path.length >= 3 &&
                action.Path[0].Name === "workbook" &&
                action.Path[1].Name === "tables" &&
                action.Path[2].Name === "add" &&
                action.Path[2].Arguments.length === 2 &&
                action.Path[2].Arguments[0].indexOf("data_cache!") === 0) {
                const localAddress = action.Path[2].Arguments[0].substring("data_cache!".length);
                action.Path.splice(1, 0, new appletMessaging.OfficeReference("worksheets", appletMessaging.OfficeReferenceType.Property));
                action.Path.splice(2, 0, new appletMessaging.OfficeReference("getItem", appletMessaging.OfficeReferenceType.Function, { Arguments: ["data_cache"] }));
                action.Path[4].Arguments[0] = localAddress;
                telemetry.events.dataCacheTableAddWorkaround.log();
            }
        }
    }

    /**
     * Handles the appletMessaging.Requests.DocumentMethodRequest event and executes the specified document method.
     * @param event The request event.
     */
    export function handleDocumentMethodRequest(event: util.AddinMessageEvent<appletMessaging.Requests.DocumentMethodRequest>): void {

        const methodInfo = event.data.Data.Method;

        // Function executed upon calling the provided document method
        function documentMethodCallback(asyncResult: Office.AsyncResult<any>) {

            // Create the response
            const response = new appletMessaging.Responses.DocumentMethodResponse(event.data);
            if (asyncResult.status === Office.AsyncResultStatus.Failed) {
                response.Status = appletMessaging.ResponseStatus.failure;
                response.FailureMessage = resources.officeInteraction.executingDocumentMethodErrorFormat(methodInfo.MethodName);
                telemetry.events.officeApiFailure.log(
                    MethodType.DocumentMethod,
                    methodInfo.MethodName,
                    (asyncResult.error || {} as any).code || -1);
            } else {
                response.Status = appletMessaging.ResponseStatus.success;
            }
            response.Data.Result = asyncResult as any;

            // Send the response back to the originating applet
            appletInteraction.sendMessageToApplet(event.data.AppletID, response);
        }

        // Get the document method
        const method = Office.context.document[methodInfo.MethodName];

        // If the method is found, proceed to call it; otherwise call the callback with a failed result
        if (method !== undefined) {

            // Get the method arguments
            const methodArgs = [];

            if (methodInfo.Arguments !== undefined) {
                for (const argument of methodInfo.Arguments) {
                    // If an argument is of the type appletMessaging.OfficeTableData or has any of its properties, the argument
                    // must be converted into the type Office.TableData or the Office API will throw an exception
                    if (argument && (argument instanceof appletMessaging.OfficeTableData || argument.rows !== undefined || argument.headers !== undefined)) {
                        const tableData = new Office.TableData();
                        tableData.rows = argument.rows;
                        tableData.headers = argument.headers;
                        methodArgs.push(tableData);
                    } else {
                        methodArgs.push(argument);
                    }
                }
            }

            // If options were provided, add them to the method arguments before the callback
            if (methodInfo.Options !== undefined) {
                methodArgs.push(methodInfo.Options);
            }

            // Add the callback to the method arguments
            methodArgs.push(documentMethodCallback);

            // Call the document method
            method.apply(Office.context.document, methodArgs);
        } else {

            // Call the callback with a failed result
            documentMethodCallback(
                {
                    status: Office.AsyncResultStatus.Failed,
                    error: {
                        message: resources.officeInteraction.officeMethodNotFoundErrorFormat(methodInfo.MethodName)
                    }
                } as Office.AsyncResult<any>);
        }
    }

    /**
     * Handles the appletMessaging.Requests.BindingsMethodRequest event and executes the specified document bindings method.
     * @param event The request event.
     */
    export function handleBindingsMethodRequest(event: util.AddinMessageEvent<appletMessaging.Requests.BindingsMethodRequest>): void {

        const methodInfo = event.data.Data.Method;

        // Function executed upon calling the provided document bindings method
        function documentMethodCallback(asyncResult: Office.AsyncResult<any>) {

            // Create the response
            const response = new appletMessaging.Responses.DocumentMethodResponse(event.data);
            if (asyncResult.status === Office.AsyncResultStatus.Failed) {
                response.Status = appletMessaging.ResponseStatus.failure;
                response.FailureMessage = resources.officeInteraction.executingBindingsMethodErrorFormat(methodInfo.MethodName);
                telemetry.events.officeApiFailure.log(
                    MethodType.BindingsMethod,
                    methodInfo.MethodName,
                    (asyncResult.error || {} as any).code || -1);
            } else {
                response.Status = appletMessaging.ResponseStatus.success;
            }
            response.Data.Result = asyncResult as any;

            // Send the response back to the originating applet
            appletInteraction.sendMessageToApplet(event.data.AppletID, response);
        }

        // Get the document bindings method
        const method = Office.context.document.bindings[methodInfo.MethodName];

        // If the method is found, proceed to call it; otherwise call the callback with a failed result
        if (method !== undefined) {

            // Get the method arguments
            let methodArgs: any[];
            if (methodInfo.Arguments === undefined) {
                methodArgs = [];
            } else {
                methodArgs = methodInfo.Arguments;
            }

            // If options were provided, add them to the method arguments before the callback
            if (methodInfo.Options !== undefined) {
                methodArgs.push(methodInfo.Options);
            }

            // Add the callback to the method arguments
            methodArgs.push(documentMethodCallback);

            // Call the document bindings method
            method.apply(Office.context.document.bindings, methodArgs);
        } else {

            // Call the callback with a failed result
            documentMethodCallback({
                status: Office.AsyncResultStatus.Failed,
                error: {
                    message: resources.officeInteraction.officeMethodNotFoundErrorFormat(methodInfo.MethodName)
                }
            } as Office.AsyncResult<any>);
        }
    }

    /**
     * Handles the appletMessaging.Requests.BindingMethodRequest event and executes the specified binding methods.
     * @param event The request event.
     */
    export function handleBindingMethodRequest(event: util.AddinMessageEvent<appletMessaging.Requests.BindingMethodRequest>): void {
        const callbackHandler = new officeInteraction.HandleBindingMethodRequestCallbackHandler(event);
        Office.context.document.bindings.getByIdAsync(event.data.Data.BindingID, {}, callbackHandler._getByIdCallback);
    }

    /** Handles callbacks for the officeInteraction.handleBindingMethodRequest method. */
    export class HandleBindingMethodRequestCallbackHandler {

        /** The request object. */
        private event: util.AddinMessageEvent<appletMessaging.Requests.BindingMethodRequest>;

        private results: Office.AsyncResult<any>[];
        private currentMethodIndex: number;
        private binding: Office.Binding;

        /**
         * @param event The request object.
         */
        constructor(event: util.AddinMessageEvent<appletMessaging.Requests.BindingMethodRequest>) {
            this.event = event;
            this.results = [];
            this.currentMethodIndex = 0;
            this._getByIdCallback = this._getByIdCallback.bind(this);
            this._bindingMethodCallback = this._bindingMethodCallback.bind(this);
        }

        /**
         * Upon successful retrieval of the binding, attempts to execute the first request table binding method. If binding retrieval
         * failed, the workflow is ended and the response is sent to the applet.
         * @param asyncResult The result of the getById call.
         */
        public _getByIdCallback(asyncResult: Office.AsyncResult<any>): void {
            this.results.push(asyncResult);
            if (asyncResult.status === Office.AsyncResultStatus.Failed) {
                const failedResponse = new appletMessaging.Responses.BindingMethodResponse(this.event.data);
                failedResponse.Status = appletMessaging.ResponseStatus.failure;
                failedResponse.FailureMessage = resources.officeInteraction.appletTableBindingRetrievalError();
                failedResponse.Data.Results = this.results;
                appletInteraction.sendMessageToApplet(this.event.data.AppletID, failedResponse);
                telemetry.events.officeApiFailure.log(
                    MethodType.BindingMethod,
                    "getByIdAsync",
                    (asyncResult.error || {} as any).code || -1);
            } else if (this.event.data.Data.Methods.length === 0) {
                const successfulResponse = new appletMessaging.Responses.BindingMethodResponse(this.event.data);
                successfulResponse.Status = appletMessaging.ResponseStatus.success;
                successfulResponse.Data.Results = this.results;
                appletInteraction.sendMessageToApplet(this.event.data.AppletID, successfulResponse);
            } else {
                this.binding = asyncResult.value;
                this._executeNextTableBindingMethod();
            }
        }

        /** Executes next table binding method in the request's Methods array and increments currentMethodIndex. */
        public _executeNextTableBindingMethod(): void {
            const methodInfo = this.event.data.Data.Methods[this.currentMethodIndex];
            const method = this.binding[methodInfo.MethodName];
            this.currentMethodIndex++;

            if (method !== undefined) {
                const methodArgs = [];

                if (methodInfo.Arguments !== undefined) {
                    for (const argument of methodInfo.Arguments) {
                        // If an argument is of the type appletMessaging.OfficeTableData or has any of its properties, the argument
                        // must be converted into the type Office.TableData or the Office API will throw an exception
                        if (argument && (argument instanceof appletMessaging.OfficeTableData || argument.rows !== undefined || argument.headers !== undefined)) {
                            const tableData = new Office.TableData();
                            tableData.rows = argument.rows;
                            tableData.headers = argument.headers;
                            methodArgs.push(tableData);
                        } else {
                            methodArgs.push(argument);
                        }
                    }
                }

                if (methodInfo.Options !== undefined) {
                    methodArgs.push(methodInfo.Options);
                }

                methodArgs.push(this._bindingMethodCallback);

                method.apply(this.binding, methodArgs);
            } else {
                this._bindingMethodCallback({
                    status: methodInfo.IsRequired ? Office.AsyncResultStatus.Failed : Office.AsyncResultStatus.Succeeded,
                    error: methodInfo.IsRequired ? {
                        message: resources.officeInteraction.officeMethodNotFoundErrorFormat(methodInfo.MethodName)
                    } : undefined
                } as Office.AsyncResult<any>);
            }
        }

        /**
         * Handling the callback for all async table binding methods other than binding retrieval. If the result is failed or
         * there are no more methods to call, the workflow is ended and the response is sent to the applet; otherwise, the
         * next method is executed on the table binding.
         * @param asyncResult The result of the table binding method call.
         */
        public _bindingMethodCallback(asyncResult: Office.AsyncResult<any>): void {

            this.results.push(asyncResult);

            // If the method failed or the current method index exceeds the bounds of the request's method array, then send the
            // response to the applet; otherwise, execute the next method in the request
            if (asyncResult.status === Office.AsyncResultStatus.Failed || this.currentMethodIndex >= this.event.data.Data.Methods.length) {
                const requestData = this.event.data.Data;
                const response = new appletMessaging.Responses.BindingMethodResponse(this.event.data);
                response.Data.Results = this.results;

                if (asyncResult.status === Office.AsyncResultStatus.Failed) {
                    const methodInfo = requestData.Methods[this.currentMethodIndex - 1];
                    response.Status = appletMessaging.ResponseStatus.failure;
                    response.FailureMessage = resources.officeInteraction.appletTableBindingMethodErrorFormat(methodInfo);
                    telemetry.events.officeApiFailure.log(
                        MethodType.BindingMethod,
                        methodInfo.MethodName,
                        (asyncResult.error || {} as any).code || -1);
                } else {
                    response.Status = appletMessaging.ResponseStatus.success;
                }

                appletInteraction.sendMessageToApplet(this.event.data.AppletID, response);
            } else {
                this._executeNextTableBindingMethod();
            }
        }
    }
}
